import { useAuthStore } from "@/stores/AuthStore";
import { useCursoStore } from "@/stores/CursoStore";
import { watch } from "vue";
import { NavigationGuardNext, RouteLocationNormalized } from "vue-router";

function passwordChangeRedirect(
  to: RouteLocationNormalized,
  authStore: ReturnType<typeof useAuthStore>
) {
  if (!authStore.user?.must_change_password || to.name === "conta") {
    return null;
  }

  return { name: "conta", query: { security: "1" } };
}

export async function requireAuth(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();
  const cursoStore = useCursoStore();

  if (!authStore.initialized) {
    await authStore.initialize();
  }

  if (!authStore.isAuthenticated) {
    return next({
      name: "login",
      query: { redirect: to.fullPath },
    });
  }

  const firstAccessRoute = passwordChangeRedirect(to, authStore);
  if (firstAccessRoute) {
    return next(firstAccessRoute);
  }

  if (!cursoStore.initialized) {
    await cursoStore.initializeCurso();
  }

  next();
}

export async function requireGuest(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();

  if (!authStore.initialized) {
    await authStore.initialize();
  }

  if (authStore.isAuthenticated) {
    const firstAccessRoute = passwordChangeRedirect(to, authStore);
    if (firstAccessRoute) {
      return next(firstAccessRoute);
    }

    return next({ name: "dashboard" });
  }

  next();
}

export function requireRole(roles: string | string[]) {
  const roleArray = Array.isArray(roles) ? roles : [roles];

  return async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const authStore = useAuthStore();
    const cursoStore = useCursoStore();

    if (!authStore.initialized) {
      await authStore.initialize();
    }

    if (!authStore.isAuthenticated) {
      return next({
        name: "login",
        query: { redirect: to.fullPath },
      });
    }

    const firstAccessRoute = passwordChangeRedirect(to, authStore);
    if (firstAccessRoute) {
      return next(firstAccessRoute);
    }

    if (!cursoStore.initialized) {
      await cursoStore.initializeCurso();
    }

    const hasRequiredRole = roleArray.some(role => authStore.hasRole(role));
    if (!hasRequiredRole) {
      return next({
        name: "unauthorized",
        query: { requiredRole: roleArray.join(",") },
      });
    }

    const currentRole = authStore.activeRole || authStore.getDefaultRole();
    const isCurrentRoleAllowed = roleArray.includes(currentRole);
    if (!isCurrentRoleAllowed) {
      return next({
        name: "unauthorized",
        query: { requiredRole: roleArray.join(","), currentRole },
      });
    }

    next();
  };
}

export function requirePermission(permission: string) {
  return async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const authStore = useAuthStore();
    const cursoStore = useCursoStore();

    if (!authStore.initialized) {
      await authStore.initialize();
    }

    if (!authStore.isAuthenticated) {
      return next({
        name: "login",
        query: { redirect: to.fullPath },
      });
    }

    const firstAccessRoute = passwordChangeRedirect(to, authStore);
    if (firstAccessRoute) {
      return next(firstAccessRoute);
    }

    if (!cursoStore.initialized) {
      await cursoStore.initializeCurso();
    }

    if (authStore.isLoading) {
      await new Promise<void>((resolve) => {
        const unwatch = watch(
          () => authStore.isLoading,
          (isLoading) => {
            if (!isLoading) {
              unwatch();
              resolve();
            }
          }
        );
      });
    }

    if (!authStore.hasPermission(permission)) {
      return next({
        name: "unauthorized",
        query: { requiredPermission: permission },
      });
    }

    next();
  };
}

export async function requireAdmin(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();
  const cursoStore = useCursoStore();

  if (!authStore.initialized) {
    await authStore.initialize();
  }

  if (!authStore.isAuthenticated) {
    return next({
      name: "login",
      query: { redirect: to.fullPath },
    });
  }

  const firstAccessRoute = passwordChangeRedirect(to, authStore);
  if (firstAccessRoute) {
    return next(firstAccessRoute);
  }

  if (!cursoStore.initialized) {
    await cursoStore.initializeCurso();
  }

  if (authStore.isLoading) {
    await new Promise<void>((resolve) => {
      const unwatch = watch(
        () => authStore.isLoading,
        (isLoading) => {
          if (!isLoading) {
            unwatch();
            resolve();
          }
        }
      );
    });
  }

  const currentRole = authStore.activeRole || authStore.getDefaultRole();
  const hasAdminRole = authStore.hasRole("admin");
  if (!hasAdminRole) {
    return next({
      name: "unauthorized",
      query: { requiredRole: "admin" },
    });
  }

  if (currentRole !== "admin") {
    return next({
      name: "unauthorized",
      query: { requiredRole: "admin", currentRole },
    });
  }

  next();
}

export async function requireCurso(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();
  const cursoStore = useCursoStore();

  if (!authStore.initialized) {
    await authStore.initialize();
  }

  if (!authStore.isAuthenticated) {
    return next({
      name: "login",
      query: { redirect: to.fullPath },
    });
  }

  const firstAccessRoute = passwordChangeRedirect(to, authStore);
  if (firstAccessRoute) {
    return next(firstAccessRoute);
  }

  if (!cursoStore.initialized) {
    await cursoStore.initializeCurso();
  }

  if (!cursoStore.cursoAtual) {
    return next({
      name: "dashboard",
      query: { error: "curso_required" },
    });
  }

  next();
}
